// Volume pricing — explicit per-unit tier tables, not percentages.
//
// Every tier states the exact price per bracket. Pack totals are unit × qty, so
// the totals are exact by construction and there is no rounding drift between
// what the site shows and what Shopify charges. Public copy shows per-unit
// pricing and tangible dollar savings — never a percentage.
//
// SHOPIFY SYNC (2026-08 redesign) — these tables are IN LOCKSTEP with the live
// Shopify automatic discounts, updated together at launch:
//   Shared automatic "amount off each" discounts on BOTH bracket products:
//     $0.25 off @20+ · $0.30 off @30+ · $0.35 off @40+ · $0.50 off @50+
//   (the old $0.15 @10+ tier was removed — 1–19 is now full price).
//   → supported ($7.50 base): 7.50 / 7.25 / 7.20 / 7.15 / 7.00
//   → flat ($7.00 base):       7.00 / 6.75 / 6.70 / 6.65 / 6.50
// One shared discount set is deliberate: Shopify applies a single automatic
// discount per order, so sharing keeps MIXED supported+flat carts pricing
// correctly. If these change, re-cut the Shopify discounts to match (and the
// dead Stripe path in backend/stripe_catalog.py if that ever goes live again).
// $7.00 is the lowest advertised automatic rate; sub-$7 is private project supply.

// Standard 2.5 mm Duragal Australian-made bracket — the entry spec.
export const PREMIUM_UNIT_PRICE = 7.5;
// Hard floor: $7.00 is the minimum public retail price per Standard bracket. No
// tier, promotion or future table may price the Standard bracket below this.
export const PREMIUM_FLOOR_PRICE = 7.0;
// Flat Slide-On Bracket (3.0 mm flat steel) — stays the cheaper option.
export const FLAT_UNIT_PRICE = 7.0;

// showSaving:false renders per-unit price only. The 10 pack saves just $1.50 —
// too small to lead with, so it sells on its per-unit price instead.
//
// PREMIUM_TIERS = Standard 2.5 mm Duragal angle. NEW 5-band ladder (2026-08
// redesign): $7.50 (1–19) → $7.25 (20–29) → $7.20 (30–39) → $7.15 (40–49) →
// $7.00 (50+). Any quantity is priced at the band it reaches.
export const PREMIUM_TIERS = [
  { min: 1, unit: 7.5, showSaving: false },
  { min: 20, unit: 7.25, showSaving: true },
  { min: 30, unit: 7.2, showSaving: true },
  { min: 40, unit: 7.15, showSaving: true },
  { min: 50, unit: 7.0, showSaving: true },
];

// Alias for readability — the Standard 2.5 mm ladder is PREMIUM_TIERS above.
export const STANDARD_TIERS = PREMIUM_TIERS;

// Heavy Duty 3.0 mm Duragal angle — the premium upgrade. Same shared per-item
// automatic discounts as the Standard bracket ($0.15/$0.30/$0.40/$0.45/$0.50 off
// each at 10/20/30/40/50+) applied to a $7.95 base, so the ladder lands exactly
// on these per-unit prices in checkout. Keep in sync with Shopify (see lib/shopify.js).
export const HEAVY_TIERS = [
  { min: 1, unit: 7.95, showSaving: false },
  { min: 10, unit: 7.8, showSaving: false },
  { min: 20, unit: 7.65, showSaving: true },
  { min: 30, unit: 7.55, showSaving: true },
  { min: 40, unit: 7.5, showSaving: true },
  { min: 50, unit: 7.45, showSaving: true },
];

// Resolve the tier table for a selected support-angle id (see data/supportAngle.js).
// Heavy Duty (3.0 mm) → HEAVY_TIERS; everything else → Standard 2.5 mm.
export function tiersForAngle(angleId) {
  return angleId === "heavy" ? HEAVY_TIERS : STANDARD_TIERS;
}

// Flat bracket ladder. The supported and flat brackets SHARE one set of Shopify
// automatic per-item discounts (Shopify applies one automatic discount per
// order, so a single shared set is what keeps mixed carts pricing correctly).
// The shared amounts-off-each are $0.25/$0.30/$0.35/$0.50 at 20/30/40/50+, so
// the flat bracket ($7.00 base) lands on the ladder below — it mirrors the
// supported ladder's reductions exactly, which is why 30/40 are $6.70/$6.65
// rather than a flat $6.75. Keep these in lockstep with the Shopify discounts.
export const FLAT_TIERS = [
  { min: 1, unit: 7.0, showSaving: false },
  { min: 20, unit: 6.75, showSaving: true },
  { min: 30, unit: 6.7, showSaving: true },
  { min: 40, unit: 6.65, showSaving: true },
  { min: 50, unit: 6.5, showSaving: true },
];

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });
export const formatAUD = (n) => fmt.format(n);

// Whole dollars render without the trailing ".00" so savings read as "Save $6"
// rather than "Save $6.00" — matches how the savings are advertised.
export const formatSaving = (n) =>
  Number.isInteger(n) ? `$${n}` : fmt.format(n);

/**
 * Pricing for a given quantity against a per-unit tier table.
 *
 * Returns the exact pack total (unit × qty), the per-unit price, the dollar
 * saved against the single-unit price, and the next tier up for the
 * "add N more" nudge. No percentages are returned — pricing is communicated as
 * per-unit price plus dollar savings.
 */
export function bracketPricing(qty, tiers = PREMIUM_TIERS) {
  const q = Math.max(1, Math.floor(Number(qty)) || 1);
  const table = tiers?.length ? tiers : PREMIUM_TIERS;
  const baseUnit = table[0].unit;

  // highest tier whose minimum quantity is met
  const tier = [...table].reverse().find((t) => q >= t.min) || table[0];
  const unitNet = tier.unit;
  const total = Number((unitNet * q).toFixed(2));
  const fullTotal = Number((baseUnit * q).toFixed(2));
  const saved = Number((fullTotal - total).toFixed(2));

  // next tier that actually costs less per unit (for the upsell nudge)
  const next = table.find((t) => t.min > q && t.unit < unitNet) || null;

  return {
    qty: q,
    unitNet,
    baseUnit,
    total,
    fullTotal,
    saved,
    // Whether this tier's saving is worth leading with in the UI.
    showSaving: Boolean(tier.showSaving) && saved > 0,
    nextTier: next
      ? { min: next.min, unit: next.unit, addMore: next.min - q }
      : null,
  };
}
